import { useEffect, useState, useCallback } from 'react';
import { useMatch, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import queryString from 'query-string';
import {
  Tabs,
  IconDownload,
  IconRefresh,
  ButtonPseudo,
  DateInputRange,
  ButtonSize,
  PermissionEnum,
  PermissionDenied,
  IconLoader,
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  Checkbox,
  ResponseError,
  getResponseErrors,
  AccountingJob,
  AccountingJobProps,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  useLazyGetProfitLossReportQuery,
  useExportProfitLossReportMutation,
  useExportProfitLossReportWithAccountsMutation,
  useLazyGetReportsSettingsQuery,
} from './store/services';
import {
  useLazyGetAccountingJobsInProgressQuery,
  useLazyGetAccountingJobsInProgressStatesQuery,
} from '../../store/dictionaries/service';
import { ProfitLoss as ProfitLossProps } from './types';
import { clearAccountingJobs } from '../../store/dictionaries/slice';

import ROUTES from '../../routes';

import cn from './profit-loss.module.css';

let timeout: NodeJS.Timeout;

const ProfitLoss = () => {
  const match = useMatch(ROUTES.REPORTS_PROFIT_lOSS);
  const sheetMatch = useMatch(ROUTES.REPORTS_PROFIT_lOSS_SHEET);
  const [dates, setDates] = useState<Date[]>([]);
  const [downloadReportVisible, setDownloadReportVisible] = useState(false);
  const [downloadReportDates, setDownloadReportDates] = useState<Date[]>([]);
  const [type, setType] = useState('without');
  const [errorList, setErrorList] = useState('');
  const [currentAccountingJob, setCurrentAccountingJob] =
    useState<AccountingJobProps>();
  const [precision, setPrecision] = useState('2');

  const [excludeRevaluation, setExcludeRevaluation] = useState<boolean>(false);

  const {
    reportsDictionaries: { activePermissions, accountingJobs },
    profitLoss: {
      sheet: { formationTime, values, accountsCategories },
      frs: { vatPercent },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { search } = useLocation();

  const [getProfitLoss, { isLoading, isFetching }] =
    useLazyGetProfitLossReportQuery();
  const [
    getReportsSettings,
    { isLoading: settingsIsLoading, isFetching: settingsIsFetching },
  ] = useLazyGetReportsSettingsQuery();
  const [
    getAccountingJobs,
    {
      isLoading: accountingJobsIsLoading,
      isFetching: accountingJobsIsFetching,
    },
  ] = useLazyGetAccountingJobsInProgressQuery();
  const [getAccountingJobsStates] =
    useLazyGetAccountingJobsInProgressStatesQuery();
  const [
    exportProfitLossReport,
    { isLoading: exportInProgress, isSuccess: exportIsSuccess },
  ] = useExportProfitLossReportMutation();

  const [
    exportProfitLossReportWithAccounts,
    {
      isLoading: exportWithAccountsInProgress,
      isSuccess: exportWithAccountsIsSuccess,
    },
  ] = useExportProfitLossReportWithAccountsMutation();

  const getProfitLossAction = useCallback(async (searchValue: string) => {
    const response = (await getProfitLoss(searchValue)) as unknown as {
      data?: ProfitLossProps;
      error?: ResponseError;
      statusCode: number;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList,
      });
    }
  }, []);

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStates = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getAccountingJobsStates(query);
      await onGetStates(query);
    }, 20000);
  };

  const onExportClick = async () => {
    const body: {
      createdAtFrom?: string;
      createdAtTo?: string;
      roundDecimals?: string;
    } = {};

    if (downloadReportDates && downloadReportDates.length) {
      body.createdAtFrom = new Date(downloadReportDates[0]).toISOString();

      if (downloadReportDates.length > 1) {
        body.createdAtTo = new Date(downloadReportDates[1]).toISOString();
      }
    }

    if (precision !== '2') {
      body.roundDecimals = precision;
    }

    if (type === 'without') {
      await exportProfitLossReport(body);
    } else if (type === 'with') {
      await exportProfitLossReportWithAccounts(body);
    }
  };

  useEffect(() => {
    if (exportIsSuccess || exportWithAccountsIsSuccess) {
      setDownloadReportVisible(false);
      setDownloadReportDates(dates);
      setType('without');

      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'profit-loss-report',
        }),
      );
    }
  }, [exportIsSuccess, exportWithAccountsIsSuccess]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_PROFIT_LOSS_LIST) &&
      !search &&
      accountsCategories
    ) {
      const date = new Date();

      date.setDate(1);
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);

      setDates([new Date(date), new Date()]);
      setDownloadReportDates([new Date(date), new Date()]);

      navigate(
        `${match || sheetMatch ? 'sheet' : 'frs'}?transferDateFrom=${format(
          new Date(date),
          'yyyy-MM-dd',
        )}&transferDateTo=${format(new Date(), 'yyyy-MM-dd')}`,
      );
    }
  }, [activePermissions, search, match, sheetMatch, accountsCategories]);

  useEffect(() => {
    if (
      accountingJobs &&
      accountingJobs?.length &&
      !accountingJobsIsFetching &&
      !accountingJobsIsLoading
    ) {
      let currentJob: AccountingJobProps | undefined;

      if (accountingJobs?.find((item) => item.state === 'IN_PROGRESS')) {
        currentJob = accountingJobs?.find(
          (item) => item.state === 'IN_PROGRESS',
        );
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        accountingJobs?.find((item) => item.state === 'QUEUED')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'QUEUED');
      } else if (
        !accountingJobs?.find((item) => item.state === 'IN_PROGRESS') &&
        !accountingJobs?.find((item) => item.state === 'QUEUED') &&
        accountingJobs?.find((item) => item.state === 'NEW')
      ) {
        currentJob = accountingJobs?.find((item) => item.state === 'NEW');
      }

      if (currentJob) {
        setCurrentAccountingJob(currentJob);
      }
    }
  }, [accountingJobs, accountingJobsIsFetching, accountingJobsIsLoading]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS)
    ) {
      getAccountingJobs(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );

      onGetStates(
        '?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW&all=true',
      );
    }
  }, [activePermissions]);

  useEffect(
    () => () => {
      dispatch(clearAccountingJobs());

      setTimeout(async () => {
        await onClearTimeout();
      });
    },
    [],
  );

  useEffect(() => {
    if (!vatPercent) {
      getReportsSettings();
    }
  }, [vatPercent]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_PROFIT_LOSS_LIST) &&
      search &&
      accountsCategories
    ) {
      const parsed = queryString.parse(search);

      const parsedDates: Date[] = [];

      if (parsed?.transferDateFrom) {
        parsedDates.push(new Date(parsed?.transferDateFrom as string));
      }

      if (parsed.transferDateTo) {
        parsedDates.push(new Date(parsed?.transferDateTo as string));
      }

      if (parsed.roundDecimals) {
        setPrecision(parsed.roundDecimals as string);
      }

      if (parsed.excludeRevaluation) {
        setExcludeRevaluation(true);
      }

      setDates(parsedDates);
      setDownloadReportDates(parsedDates);

      getProfitLossAction(search);
    }
  }, [activePermissions, search, accountsCategories]);

  useEffect(() => {
    let title = 'Profit-Loss report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = (
    <div className={cn.profitLossLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      !activePermissions.includes(PermissionEnum.REPORTS_PROFIT_LOSS_LIST) ||
      !activePermissions.includes(PermissionEnum.SECTION_REPORTS)
    ) {
      content = <PermissionDenied />;
    } else {
      content = (
        <div className={cn.profitLoss}>
          <div className={cn.profitLossTop}>
            <div className={cn.profitLossTitle}>Profit-Loss</div>
            <div className={cn.profitLossTopControls}>
              <div className={cn.profitLossTopControlsLeft}>
                <Tabs
                  wrapperClassName={cn.profitLossTopTabs}
                  tabs={[
                    {
                      name: 'Profit-Loss',
                      path: `${ROUTES.REPORTS_PROFIT_lOSS_SHEET}${
                        search || ''
                      }`,
                    },
                    {
                      name: 'FRS 102',
                      path: `${ROUTES.REPORTS_PROFIT_lOSS_FRS}${search || ''}`,
                    },
                  ]}
                  enableRouting
                />
                <DateInputRange
                  placeholder="All time"
                  values={dates.map((item) => item.toISOString())}
                  className={cn.profitLossDateInput}
                  onReset={() => {
                    let link = sheetMatch ? 'sheet' : 'frs';

                    if (excludeRevaluation) {
                      link = `${link}?excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);

                    setDates([]);
                    setDownloadReportDates([]);
                  }}
                  onChangeRange={(dateValues) => {
                    setDates(dateValues);
                    setDownloadReportDates(dateValues);

                    let link = `?transferDateFrom=${format(
                      new Date(dateValues[0]),
                      'yyyy-MM-dd',
                    )}`;

                    if (dateValues.length > 1) {
                      link = `${link}&transferDateTo=${format(
                        new Date(dateValues[1]),
                        'yyyy-MM-dd',
                      )}`;
                    }

                    if (excludeRevaluation) {
                      link = `${link}&excludeRevaluation=${excludeRevaluation}`;
                    }

                    if (precision !== '2') {
                      link = `${link}&roundDecimals=${precision}`;
                    }

                    navigate(link);
                  }}
                />
              </div>
              <div className={cn.profitLossTopControlsRight}>
                <ButtonPseudo
                  text="Refresh"
                  icon={
                    <IconRefresh
                      className={isFetching && values ? cn.refreshing : ''}
                    />
                  }
                  className={`button-white ${cn.profitLossRefreshButton}`}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    getProfitLossAction(search);
                    setErrorList('');
                  }}
                  disabled={isLoading || isFetching}
                />
                {activePermissions?.includes(
                  PermissionEnum.REPORTS_PROFIT_LOSS_EXPORT_ACTION,
                ) && (
                  <ButtonPseudo
                    text="Download Report"
                    icon={<IconDownload />}
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={() => setDownloadReportVisible(true)}
                    disabled={Boolean(errorList)}
                  />
                )}
              </div>
            </div>
            <div className={cn.profitLossTopSearchInfo}>
              {formationTime?.end
                ? `Formed in ${formationTime?.duration} sec: ${new Date(
                    formationTime?.start || '',
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric',
                    day: 'numeric',
                  })} from ${new Date(formationTime?.start || '')
                    .toLocaleString()
                    .slice(-8)} to ${new Date(formationTime?.end)
                    .toLocaleString()
                    .slice(-8)}`
                : 'Forming...'}
            </div>
            {activePermissions?.includes(
              PermissionEnum.REPORTS_ACCOUNTING_JOBS_LITS,
            ) && (
              <AccountingJob
                accountingJobsIsLoading={
                  accountingJobsIsLoading || accountingJobsIsFetching
                }
                accountingJob={currentAccountingJob}
                accountingJobs={accountingJobs}
                jobsLength={accountingJobs?.length}
                query="?type=BALANCES_LOGS&type=BALANCE_SHEET&type=REVALUATION&state=IN_PROGRESS&state=QUEUED&state=NEW"
              />
            )}
          </div>
          <Outlet
            context={{
              isLoading:
                isLoading ||
                isFetching ||
                settingsIsLoading ||
                settingsIsFetching,
              errorList,
              excludeRevaluation,
              setExcludeRevaluation,
              dates,
              precision,
              setPrecision,
            }}
          />
          <SidePanel
            title="Download report"
            isOpen={downloadReportVisible}
            onClose={() => {
              setDownloadReportVisible(false);
              setDownloadReportDates(dates);
              setType('without');
            }}
            sidepanelExpandedKey="download-report-panel"
            type={SidePanelType.AML}
            footer={
              <div className={cn.statementPanelControls}>
                <ButtonPrimary
                  text={
                    exportInProgress || exportWithAccountsInProgress
                      ? 'Loading...'
                      : 'Download'
                  }
                  icon={<IconDownload />}
                  disabled={exportInProgress || exportWithAccountsInProgress}
                  onClick={onExportClick}
                  bold
                />
                <ButtonSecondary
                  className="button-white"
                  text="Close"
                  onClick={() => {
                    setDownloadReportDates(dates);
                    setDownloadReportVisible(false);
                    setType('without');
                  }}
                  bold
                />
              </div>
            }
            closeOnClickOutside
          >
            <>
              <div className={cn.exportTypeTitle}>Export type</div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="without"
                  title="Without accounts"
                  checked={type === 'without'}
                  onChange={() => setType('without')}
                />
              </div>
              <div className={cn.radioWrapper}>
                <Checkbox
                  name="type"
                  type="radio"
                  value="with"
                  title="With accounts"
                  checked={type === 'with'}
                  onChange={() => setType('with')}
                />
              </div>
              <div
                className={`${cn.datesWrapper} ${
                  !downloadReportDates.length ? cn.empty : ''
                }`}
              >
                <div className={cn.dateInputTitle}>Report period</div>
                <DateInputRange
                  values={downloadReportDates?.map((item) =>
                    item.toISOString(),
                  )}
                  onChangeRange={(date) => setDownloadReportDates(date)}
                  placeholder="Select date"
                  withoutTwoPickers
                  withoutCustomSelector
                />
              </div>
            </>
          </SidePanel>
        </div>
      );
    }
  }

  return content;
};

export default ProfitLoss;
